// src/components/customer/BatchPaymentSection.js
import React, { useState, useMemo } from 'react';
import { useBatchPayment } from '../../hooks/useBatchPayment';
import { Badge } from '../common';
import { Service } from '../../models/Service';

/**
 * Batch Payment Section Component
 * Allows selecting and paying multiple unpaid appointments in a single payment
 */
export const BatchPaymentSection = ({
  appointments,
  customerId,
  customerName,
  customerPhone,
  customerEmail,
  onPaymentCreated
}) => {
  const [showPaymentUrl, setShowPaymentUrl] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [batchInvoiceText, setBatchInvoiceText] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  const {
    selectedAppointments,
    selectedCount,
    totalAmount,
    loading,
    error,
    paymentUrl,
    toggleAppointment,
    selectAll,
    deselectAll,
    isSelected,
    createBatchPayment,
    reset
  } = useBatchPayment(appointments, {
    customerName,
    customerPhone,
    customerEmail
  });

  // Filter and sort unpaid appointments (exclude paid, cancelled, no_show_justified, and services without payment)
  const unpaidAppointments = useMemo(() => {
    const filtered = appointments.filter(apt =>
      !apt.is_paid && apt.status !== 'cancelled' && apt.status !== 'no_show_justified' && !Service.getServicesWithoutPayment().includes(apt.service_name)
    );

    // Sort by date and time
    return filtered.sort((a, b) => {
      const dateTimeA = new Date(`${a.date}T${a.time}`);
      const dateTimeB = new Date(`${b.date}T${b.time}`);

      if (sortOrder === 'desc') {
        return dateTimeB - dateTimeA; // Most recent first
      } else {
        return dateTimeA - dateTimeB; // Oldest first
      }
    });
  }, [appointments, sortOrder]);

  if (unpaidAppointments.length === 0) {
    return null; // Don't show section if no unpaid appointments
  }

  const handleCreatePayment = async () => {
    const customText = batchInvoiceText.trim();
    const result = await createBatchPayment(customText);
    if (result && onPaymentCreated) {
      onPaymentCreated(result.paymentUrl);
    }
    if (result) {
      setShowPaymentUrl(true);
    }
  };

  const handleReset = () => {
    reset();
    setShowPaymentUrl(false);
    setBatchInvoiceText('');
    setCopySuccess(false);
  };

  const handleCopyPaymentLink = async () => {
    try {
      await navigator.clipboard.writeText(paymentUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error('Failed to copy payment link:', err);
    }
  };

  const formatDateTime = (date, time) => {
    const appointmentDate = new Date(`${date}T${time}`);
    const options = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };
    return appointmentDate.toLocaleDateString('he-IL', options).replace(',', ' בשעה ');
  };

  const formatCurrency = (amount) => {
    const num = Number(amount);
    return `₪${isNaN(num) ? '0.00' : num.toFixed(2)}`;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">💳 תשלום מרוכז</h2>
        {!showPaymentUrl && unpaidAppointments.length > 0 && (
          <div className="text-sm text-gray-600">
            {unpaidAppointments.length} תורים ממתינים לתשלום
          </div>
        )}
      </div>

      {/* Success state - show payment URL */}
      {paymentUrl && showPaymentUrl ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-green-900 mb-2">קישור תשלום נוצר בהצלחה!</h3>
              <p className="text-green-800 mb-2">
                סכום כולל: <strong>{formatCurrency(totalAmount)}</strong> עבור {selectedCount} תורים
              </p>
              <p className="text-green-700 text-sm mb-4">
                הקישור נשלח ללקוח. לאחר פתיחת הקישור, הוא תקף למשך 10 דקות.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleCopyPaymentLink}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white transition-colors ${
                    copySuccess
                      ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                      : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                >
                  {copySuccess ? (
                    <>
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      הקישור הועתק!
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      העתק קישור תשלום
                    </>
                  )}
                </button>
                <button
                  onClick={handleReset}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  צור תשלום חדש
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Selection controls */}
          {selectedCount > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium text-blue-900">נבחרו {selectedCount} תורים</span>
                    <span className="text-blue-700 mr-2">• סכום: {formatCurrency(totalAmount)}</span>
                  </div>
                </div>
                <button
                  onClick={deselectAll}
                  className="text-sm text-blue-700 hover:text-blue-900 font-medium transition-colors"
                >
                  בטל בחירה
                </button>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-sm text-red-800">{error}</div>
              </div>
            </div>
          )}

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto mb-4">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-right py-3 px-4 font-medium text-gray-700" style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={selectedCount === unpaidAppointments.length && selectedCount > 0}
                      onChange={() => {
                        if (selectedCount === unpaidAppointments.length) {
                          deselectAll();
                        } else {
                          selectAll();
                        }
                      }}
                    />
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">שירות</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">
                    <button
                      onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                      className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                      title={sortOrder === 'desc' ? 'מיין מהישן לחדש' : 'מיין מהחדש לישן'}
                    >
                      תאריך ושעה
                      <svg
                        className={`w-4 h-4 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">איש צוות</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">מחיר</th>
                </tr>
              </thead>
              <tbody>
                {unpaidAppointments.map((apt) => (
                  <tr
                    key={apt.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                      isSelected(apt.id) ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => toggleAppointment(apt.id)}
                  >
                    <td className="py-4 px-4" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={isSelected(apt.id)}
                        onChange={() => toggleAppointment(apt.id)}
                      />
                    </td>
                    <td className="py-4 px-4">
                      <div className="font-medium text-gray-900 text-right">
                        {apt.service_name || apt.serviceName}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-gray-900 text-right">
                        {formatDateTime(apt.date, apt.time)}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-gray-900 text-right">
                        {apt.crew_member_name || '—'}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="font-medium text-gray-900 text-right">
                        {formatCurrency(apt.price)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden mb-4" dir="rtl">
            {/* Sort control for mobile */}
            <div className="flex items-center justify-between mb-3 px-2">
              <span className="text-sm font-medium text-gray-700">מיון לפי תאריך:</span>
              <button
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 transition-colors"
              >
                {sortOrder === 'desc' ? 'חדש לישן' : 'ישן לחדש'}
                <svg
                  className={`w-4 h-4 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            <div className="space-y-3">
              {unpaidAppointments.map((apt) => (
                <div
                  key={apt.id}
                  className={`border rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    isSelected(apt.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => toggleAppointment(apt.id)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={isSelected(apt.id)}
                        onChange={() => toggleAppointment(apt.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="font-medium text-gray-900">
                        {apt.service_name || apt.serviceName}
                      </div>
                    </div>
                    <div className="font-medium text-gray-900">
                      {formatCurrency(apt.price)}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 mb-1 text-right">
                    {formatDateTime(apt.date, apt.time)}
                  </div>
                  {apt.crew_member_name && (
                    <div className="text-sm text-gray-600 text-right">
                      {apt.crew_member_name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Optional Batch Invoice Text */}
          {selectedCount > 0 && (
            <div className="mt-6 space-y-2 pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700">
                תוספת טקסט לחשבונית (יחול על כל התורים, אופציונלי)
              </label>
              <input
                type="text"
                value={batchInvoiceText}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 100) {
                    setBatchInvoiceText(value);
                  }
                }}
                maxLength={100}
                placeholder="לדוגמה: Jay junior"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                dir="auto"
              />
              <div className="flex items-start justify-between text-xs text-gray-500">
                <span>{batchInvoiceText.length}/100 תווים</span>
              </div>
              {/* Preview */}
              {batchInvoiceText.trim() && selectedAppointments.length > 0 && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-gray-600 mb-2">תצוגה מקדימה לחשבונית:</p>
                  <div className="space-y-1">
                    {unpaidAppointments
                      .filter(apt => selectedAppointments.includes(apt.id))
                      .slice(0, 3)
                      .map((apt, index) => {
                        const dateStr = formatDateTime(apt.date, apt.time).split(' בשעה ')[0];
                        return (
                          <p key={index} className="text-sm text-gray-900">
                            • {apt.service_name} - {batchInvoiceText} - {dateStr}
                          </p>
                        );
                      })}
                    {selectedAppointments.length > 3 && (
                      <p className="text-xs text-gray-500 mt-1">
                        ...ועוד {selectedAppointments.length - 3} תורים
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action button */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div>
              {selectedCount > 0 && (
                <div className="text-xl font-semibold text-gray-900">
                  סה"כ לתשלום: <span className="text-blue-600">{formatCurrency(totalAmount)}</span>
                </div>
              )}
            </div>
            <button
              onClick={handleCreatePayment}
              disabled={selectedCount === 0 || loading}
              className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white transition-colors ${
                selectedCount === 0 || loading
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 ml-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  יוצר קישור תשלום...
                </>
              ) : (
                <>
                  💳 שלח קישור תשלום ({selectedCount})
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default BatchPaymentSection;
